import React, { useState, useEffect, useLayoutEffect, useRef, useId } from 'react';
import { motion } from 'framer-motion';
import { useTheme } from '../context/ThemeContext';
import { useProfile } from '../context/ProfileContext';
import { useT } from '../i18n/useT';
import { F, DUR, EASE } from '../styles/theme';
import { usePress } from '../components/red/Press';
import { rulesFor } from '../utils/taxRules';
import { Icon, Icons } from '../components/red/Icons';
import { NavGlyph } from '../components/red/NavGlyph';
import NotificationBell from '../components/NotificationBell';
import QuickSettingsMenu from '../components/red/QuickSettingsMenu';
import CommandPalette, { useCommandPaletteHotkey } from '../components/CommandPalette';

const RED = '#E4544A';
const REDUCE_MOTION = typeof matchMedia !== 'undefined'
  && matchMedia('(prefers-reduced-motion: reduce)').matches;
const TOPBAR_H = 74;

// Schwebende neutrale Glass-Insel (vertikale Rail) — Pendant zur Mobile-Pill.
// Fläche folgt dem Theme (dunkel/hell); Rot kehrt als gezielter Akzent zurück:
// aktiver Tab rot hinterlegt, „+" rot — sonst neutral.
const ISLE_BLUR = 'blur(30px) saturate(180%)';
const RED_BTN = '#E4544A'; // Marken-Koralle (flach, Tiefe via Schatten)
function navGlass(isDark) {
  return isDark
    ? { bg: 'rgba(18,20,26,0.86)',  line: 'rgba(255,255,255,0.07)', edge: 'rgba(255,255,255,0.04)' }
    : { bg: 'rgba(220,220,218,0.94)', line: 'rgba(0,0,0,0.07)',    edge: 'rgba(255,255,255,0.80)' };
}
const RAIL_W = 86;
const RAIL_M = 16;

// Browser-Zoom-Faktor: direkt via devicePixelRatio (Strg+Mausrad feuert resize
// und ändert DPR in Chrome/Edge). Wir korrigieren per CSS `scale` (Individual
// Transform Property) — OHNE `transition`, damit die Korrektur frame-sofort
// greift und nicht über 0.3s nachläuft. `translate` (Rail-Toggle) animiert
// weiterhin unabhängig davon.
function useZoom() {
  const [zoom, setZoom] = useState(1);
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const base = window.devicePixelRatio || 1;
    const update = () => setZoom((window.devicePixelRatio || 1) / base);
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);
  return zoom;
}

// Vertikales Rail-Item — Dick & Rund-Glyph. Aktiv = rot hinterlegte Pille +
// weißes Icon; inaktiv neutral (Theme-Farbe), Hover hellt auf.
function RailItem({ id, label, active, onClick, thumbId, tourId }) {
  const { T } = useTheme();
  const [hover, setHover] = useState(false);
  const { pressTransform, pressHandlers } = usePress({ scale: 0.9 });
  // Calm: aktiver Tab neutral (kein rotes Pill mehr) — Rot bleibt allein dem „+".
  const iconColor = active ? T.text : (hover ? T.text : T.sub);
  return (
    <button
      data-tour={tourId}
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      {...pressHandlers}
      aria-label={label}
      style={{
        width: 66,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '7px 0',
        border: 'none',
        background: 'transparent',
        cursor: 'pointer',
        // z-index-Falle: aktiver Button BEWUSST unter den inaktiven (0<1), damit
        // framers temporäre z-Anhebung des gleitenden layoutId-Thumbs im aktiven
        // Element gefangen bleibt → der rote Thumb gleitet UNTER fremden Icons
        // durch, statt sie kurz zu überdecken.
        position: 'relative',
        zIndex: active ? 0 : 1,
        transform: pressTransform,
        WebkitTapHighlightColor: 'transparent',
        transition: `transform ${DUR.press}ms ${EASE}`,
      }}
    >
      <span
        style={{
          position: 'relative',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          width: 46, height: 40, borderRadius: 13,
          background: (hover && !active) ? T.itemHover : 'transparent',
          transition: `background-color ${DUR.fast}ms ease`,
        }}
      >
        {active && (
          <motion.div
            layoutId={thumbId}
            // Bounce-freier Glide auf der App-EASE. Im Ruhezustand füllt der Thumb
            // die Pille (inset:0) EXAKT → pixelgenau zentriert bei jedem Zoom/dpr
            // (kein gemessenes Overlay). Nur beim Tab-Wechsel gleitet framer ihn
            // per FLIP von der alten zur neuen Pille.
            transition={{ duration: 0.42, ease: [0.16, 1, 0.3, 1] }}
            style={{ position: 'absolute', inset: 0, borderRadius: 13, background: T.glassHigh, zIndex: 0 }}
          />
        )}
        <span style={{ position: 'relative', zIndex: 1, display: 'flex' }}>
          <NavGlyph id={id} active={false} stroke={iconColor} size={24} sw={1.7} />
        </span>
      </span>
    </button>
  );
}

function TopChipBtn({ icon, onClick, title, active, dot }) {
  const { T } = useTheme();
  const { pressTransform, pressHandlers } = usePress({ scale: 0.96 });
  return (
    <button
      onClick={onClick}
      aria-label={title}
      onMouseEnter={e => { e.currentTarget.style.color = T.text; }}
      onMouseLeave={e => { e.currentTarget.style.color = active ? T.text : T.sub; }}
      {...pressHandlers}
      style={{
        position: 'relative',
        width: 38,
        height: 38,
        background: 'transparent',
        border: 'none',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        color: active ? T.text : T.sub,
        padding: 0,
        transform: pressTransform,
        transition: `color ${DUR.fast}ms ease, transform ${DUR.press}ms ${EASE}`,
      }}
    >
      <Icon d={icon} size={20} stroke="currentColor" sw={2.4} />
      {dot && (
        <span style={{
          position: 'absolute',
          top: 9, right: 9,
          width: 6, height: 6,
          borderRadius: 3,
          background: RED,
        }} />
      )}
    </button>
  );
}

/**
 * Desktop-/Tablet-Layout-Shell.
 * Navigation = schwebende Rot-Glass-Rail links (kein ausfahrbares Modul mehr).
 * Mobile bleibt unberührt — App.js entscheidet per useBreakpoint.
 */
export default function DesktopShell({ tab, scrollKey, onTabChange, onBack, backTitle, onOpenAlerts, onOpenSupport, txs, marketData, onSelectAsset, children }) {
  const { T, isDark } = useTheme();
  const g = navGlass(isDark);
  // Rail bei Browser-Zoom gegen-skalieren (physische Größe konstant halten).
  // Clamp gegen Extreme, damit sie bei sehr starkem Raus-/Reinzoomen weder
  // riesig wird noch verschwindet.
  const zoom = useZoom();
  const railScale = Math.min(1.5, Math.max(0.72, 1 / zoom));
  const railThumbId = useId(); // gleitender Aktiv-Thumb (framer layoutId), rail-weit geteilt
  const { country } = useProfile();
  const showSmartCheck = rulesFor(country).showSmartCheck;
  const { t } = useT();
  const [paletteOpen, setPaletteOpen] = useState(false);
  useCommandPaletteHotkey(setPaletteOpen);

  // Rail ein-/ausklappbar (rechts), Zustand persistiert.
  const [railHidden, setRailHidden] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem('red.nav.hidden') === '1';
  });
  useEffect(() => {
    try { window.localStorage.setItem('red.nav.hidden', railHidden ? '1' : '0'); } catch {}
  }, [railHidden]);

  const mainRef = useRef(null);
  useLayoutEffect(() => {
    if (mainRef.current) mainRef.current.scrollTop = 0;
  }, [scrollKey]);

  // Aktiv-Markierung = rote Fläche DIREKT am Pillen-Span (siehe RailItem) — kein
  // separater, gemessener Thumb mehr. Das Icon ist Kind dieser Fläche und damit
  // IMMER pixelgenau zentriert (bei jedem Zoom/dpr/Resize). Der frühere
  // floating-Thumb (CSS-transform aus gemessener Pillen-Position) konnte um 1–2px
  // driften, wenn sich das Layout nach dem Positionieren verschob → wirkte
  // „nicht zentriert". Crossfade statt Quer-Glide; der Icon-Pop (NavGlyph) bleibt.

  // Nav-Struktur (Reihenfolge wie zuvor). Icons kommen jetzt aus NavGlyph (per id).
  const primaryNav = [
    { id: 'dashboard', label: t('dashboard') },
    { id: 'wallet',    label: t('transactions') },
    { id: 'markets',   label: t('markets') },
    ...(showSmartCheck ? [{ id: 'berichte', label: t('smart_check') }] : []),
    { id: 'reports',   label: t('reports') },
    { id: 'alerts',    label: t('alerts') },
  ];
  const bottomNav = [
    { id: 'pricing',  label: t('plans') },
    { id: 'settings', label: t('profile') },
  ];
  const navItems = [...primaryNav, ...bottomNav]; // für Title-Lookup im TopBar

  return (
    <div
      style={{
        height: '100vh',
        position: 'relative',
        overflow: 'hidden',
        background: T.bg,
        color: T.text,
        fontFamily: F.family,
      }}
    >
      {/* ── Schwebende Rot-Glass-Rail ───────────────────────────────
           Vertikale Zentrierung via Flex-Wrapper (full-height, kein Layout-
           Platz, pointer-events durchlässig). Rail nutzt CSS Individual
           Transform Properties: `translate` animiert (Ein-/Ausklappen),
           `scale` snappt sofort (Zoom-Gegenrechnung ohne Verzögerung). */}
      <div
        style={{
          position: 'fixed',
          left: RAIL_M,
          top: 0,
          height: '100vh',
          display: 'flex',
          alignItems: 'center',
          zIndex: 30,
          pointerEvents: 'none',
        }}
      >
      <aside
        style={{
          width: RAIL_W,
          borderRadius: 26,
          background: g.bg,
          backdropFilter: ISLE_BLUR,
          WebkitBackdropFilter: ISLE_BLUR,
          boxShadow: `inset 0 1px 0 ${g.edge}, inset 0 0 0 1px ${g.line}, 0 18px 40px rgba(0,0,0,0.22)`,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          padding: '16px 0 12px',
          pointerEvents: 'auto',
          transformOrigin: 'left center',
          // CSS Individual Transform Properties: `translate` animiert (Rail-Toggle),
          // `scale` ist absichtlich OHNE transition → Zoom-Korrektur greift sofort
          // ohne sichtbares Nachlaufen (alte combined `transform`-transition ließ
          // die Gegenrechnung 340ms nach = „Sprung"-Effekt).
          translate: `${railHidden ? 'calc(-100% - 28px)' : '0px'} 0`,
          scale: `${railScale}`,
          transition: `translate ${DUR.slow}ms cubic-bezier(.4,0,.2,1)`,
        }}
      >
        {/* Wortmarke wandert in den Kopf (Calm-Sprache) — Rail bleibt rein funktional. */}

        {/* Add-CTA — die EINE rote Aktion (Rot konzentriert: „+" + Wortmarke oben) */}
        <button
          onClick={() => onTabChange('add')}
          aria-label={t('transaction')}
          data-tour="nav-fab-add"
          onPointerDown={e => { if (!REDUCE_MOTION) e.currentTarget.style.transform = 'scale(0.92)'; }}
          onPointerUp={e => e.currentTarget.style.transform = 'scale(1)'}
          onPointerCancel={e => e.currentTarget.style.transform = 'scale(1)'}
          style={{
            width: 48, height: 48, borderRadius: 16,
            border: 'none', cursor: 'pointer',
            // Der eine rote Akzent — die Hauptaktion.
            background: RED_BTN,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 1px 2px rgba(0,0,0,0.16), inset 0 1px 0 rgba(255,255,255,0.16)',
            marginBottom: 16, flexShrink: 0,
            WebkitTapHighlightColor: 'transparent',
            transition: `transform ${DUR.press}ms ${EASE}`,
          }}
        >
          <Icon d={Icons.plus} size={21} stroke="#fff" sw={2.6} />
        </button>

        {/* Primary Nav */}
        <nav style={{ display: 'flex', flexDirection: 'column', gap: 3, width: '100%', alignItems: 'center' }}>
          {primaryNav.map(it => (
            <RailItem key={it.id} id={it.id} label={it.label} active={tab === it.id} onClick={() => onTabChange(it.id)} thumbId={railThumbId} tourId={`nav-${it.id}`} />
          ))}
        </nav>

        {/* kleiner Abstand + Hairline zu den unteren Buttons */}
        <div style={{ width: 40, height: 1, background: g.line, margin: '12px 0' }} />

        {/* Bottom Nav (Tarife, Profil) */}
        <nav style={{ display: 'flex', flexDirection: 'column', gap: 3, width: '100%', alignItems: 'center' }}>
          {bottomNav.map(it => (
            <RailItem key={it.id} id={it.id} label={it.label} active={tab === it.id} onClick={() => onTabChange(it.id)} thumbId={railThumbId} tourId={`nav-${it.id}`} />
          ))}
        </nav>

        {/* Einklappen — schiebt die Rail nach rechts raus */}
        <button
          onClick={() => setRailHidden(true)}
          aria-label={t('collapse_sidebar')}
          onMouseEnter={e => { e.currentTarget.style.color = T.text; }}
          onMouseLeave={e => { e.currentTarget.style.color = T.sub; e.currentTarget.querySelector('svg').style.transform = 'scale(1)'; }}
          onMouseDown={e => { e.currentTarget.querySelector('svg').style.transform = 'scale(0.92)'; }}
          onMouseUp={e => { e.currentTarget.querySelector('svg').style.transform = 'scale(1)'; }}
          style={{
            marginTop: 10, width: 30, height: 30,
            border: 'none', cursor: 'pointer',
            background: 'transparent',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
            WebkitTapHighlightColor: 'transparent',
            color: T.sub, transition: `color ${DUR.fast}ms ease`,
          }}
        >
          <Icon d={Icons.chevL} size={16} stroke="currentColor" sw={2.4} style={{ transition: `transform ${DUR.fast}ms ${EASE}` }} />
        </button>
      </aside>
      </div>

      {/* Reveal-Handle am rechten Rand — nur wenn eingeklappt */}
      {railHidden && (
        <button
          onClick={() => setRailHidden(false)}
          aria-label={t('expand_sidebar')}
          onMouseEnter={e => { e.currentTarget.style.color = T.text; }}
          onMouseLeave={e => { e.currentTarget.style.color = T.sub; e.currentTarget.querySelector('svg').style.transform = 'scale(1)'; }}
          onMouseDown={e => { e.currentTarget.querySelector('svg').style.transform = 'scale(0.92)'; }}
          onMouseUp={e => { e.currentTarget.querySelector('svg').style.transform = 'scale(1)'; }}
          style={{
            position: 'fixed', left: 0, top: '50%',
            translate: '0 -50%', scale: `${railScale}`, transformOrigin: 'left center',
            zIndex: 30, width: 22, height: 66,
            borderRadius: '0 13px 13px 0', border: 'none', cursor: 'pointer',
            background: g.bg,
            backdropFilter: ISLE_BLUR, WebkitBackdropFilter: ISLE_BLUR,
            boxShadow: `inset 0 1px 0 ${g.edge}, inset 0 0 0 1px ${g.line}, 4px 0 16px rgba(0,0,0,0.20)`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            WebkitTapHighlightColor: 'transparent',
            color: T.sub, transition: `color ${DUR.fast}ms ease`,
          }}
        >
          <Icon d={Icons.chevR} size={16} stroke="currentColor" sw={2.4} style={{ transition: `transform ${DUR.fast}ms ${EASE}` }} />
        </button>
      )}

      {/* ── Main column ── Volle Breite: Topbar durchgehend, Content im echten
           Viewport zentriert. Die Rail ist ein Overlay (kein Layout-Platz) →
           Content bleibt statisch beim Ein-/Ausklappen. ── */}
      <div style={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
      }}>
        <header
          style={{
            position: 'sticky',
            top: 0,
            height: TOPBAR_H,
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '0 30px',
            borderBottom: `1px solid ${T.line}`,
            background: T.navBg || T.bg,
            backdropFilter: T.navBlur,
            WebkitBackdropFilter: T.navBlur,
            zIndex: 4,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ fontFamily: F.brand, fontSize: 20, fontWeight: 800, letterSpacing: '-0.05em', userSelect: 'none', flexShrink: 0 }}>
              <span style={{ color: T.text }}>red</span><span aria-hidden style={{ display: 'inline-block', width: 3.6, height: 3.6, marginLeft: 1.8, background: RED, borderRadius: 0.72, verticalAlign: 'baseline' }} />
            </span>
            <span aria-hidden style={{ width: 1, height: 18, background: T.line, flexShrink: 0 }} />
            <h1 style={{
              fontFamily: F.display,
              fontSize: 21,
              fontWeight: 800,
              letterSpacing: '-0.035em',
              color: T.text,
              margin: 0,
              lineHeight: 1,
            }}>
              {backTitle || navItems.find(n => n.id === tab)?.label || ''}
            </h1>
          </div>

          {/* Center: Such-Pill öffnet Command Palette */}
          <button
            onClick={() => setPaletteOpen(true)}
            aria-label={`${t('quick_search')} (⌘K)`}
            style={{
              position: 'absolute',
              left: '50%',
              transform: 'translateX(-50%)',
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '9px 14px',
              minWidth: 240,
              maxWidth: 380,
              background: T.surfaceHi || T.surface,
              border: `1px solid ${T.line}`,
              borderRadius: 12,
              color: T.mute,
              cursor: 'pointer',
              fontFamily: F.family,
              fontSize: 13,
              fontWeight: 500,
              letterSpacing: '-0.005em',
              WebkitTapHighlightColor: 'transparent',
            }}
          >
            <svg width={15} height={15} viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth={2} strokeLinecap="round" style={{ flexShrink: 0 }}>
              <circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" />
            </svg>
            <span style={{ flex: 1, textAlign: 'left' }}>{t('search_placeholder')}</span>
          </button>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <TopChipBtn icon={Icons.headset} onClick={() => onOpenSupport?.()} title={t('support')} />
            <span aria-hidden style={{ width: 1, height: 20, background: T.line, margin: '0 2px' }} />
            <NotificationBell variant="chip" onManage={onOpenAlerts} />
            <QuickSettingsMenu />
          </div>
        </header>

        <main
          ref={mainRef}
          className="hide-scrollbar"
          style={{
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            padding: '28px 40px 56px',
          }}
        >
          <div style={{ maxWidth: 1280, margin: '0 auto' }}>
            {/* Kontextueller Zurück — Pfeil-only-Rundbutton (kompakt, kein Text).
                Für Sub-Views ohne eigenen Coin-Kopf (Alerts/Wizards). AssetDetail
                rendert den Pfeil inline neben dem Coin-Logo; Märkte braucht keinen
                (Navbar genügt) — beide via App.js von onBack ausgenommen. */}
            {onBack && (
              <button
                onClick={onBack}
                aria-label={t('back')}
                title={t('back')}
                style={{
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  width: 38, height: 38, marginBottom: 18,
                  background: T.surfaceHi || T.surface,
                  border: `1px solid ${T.line}`, borderRadius: 999,
                  color: T.sub, cursor: 'pointer',
                  WebkitTapHighlightColor: 'transparent',
                  transition: `color ${DUR.fast}ms ease, background ${DUR.fast}ms ease`,
                }}
                onMouseEnter={e => { e.currentTarget.style.color = T.text; e.currentTarget.style.background = T.itemHover; }}
                onMouseLeave={e => { e.currentTarget.style.color = T.sub; e.currentTarget.style.background = T.surfaceHi || T.surface; }}
              >
                <Icon d={Icons.back} size={17} stroke="currentColor" sw={2.4} />
              </button>
            )}
            {children}
          </div>
        </main>
      </div>

      <CommandPalette
        open={paletteOpen}
        onClose={() => setPaletteOpen(false)}
        onTabChange={onTabChange}
        onOpenAlerts={onOpenAlerts}
        txs={txs}
        marketData={marketData}
        onSelectAsset={onSelectAsset}
      />
    </div>
  );
}
